import {Alert, Box, Button, Dialog, DialogContent, DialogTitle, useTheme} from "@mui/material";
import {FiPlus, FiRefreshCw} from "react-icons/fi";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import SearchBar from "../components/ui/SearchBar";
import LoadingContent from "../components/ui/LoadingContent";
import TicketForm from "../components/Tickets/TicketForm";
import {ticketColumns} from "../constants/dataGridColumns";
import {TICKET_SERVER_FILTER_KEYS, ticketServerSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import {ACTIONS, RESOURCES, useAuthPermissions} from "../hooks/useAuthPermissions";
import {isForbiddenError} from "../utils/apiError";
import {fetchAllPages} from "../utils/pagination";
import {
  TICKETS_MAX_PAGE_SIZE,
  buildProductNameMap,
  decorateTickets,
  filterTicketsClientSide,
  getProductFilterOptions
} from "../utils/tickets";

const pickServerFilters = (filters = {}) =>
  TICKET_SERVER_FILTER_KEYS.reduce((params, key) => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") params[key] = filters[key];
    return params;
  }, {});

const Tickets = () => {
  const theme = useTheme();
  const {can} = useAuthPermissions();

  const {list: listTickets} = useApiRequest({baseUrl: "/tickets", resource: "tickets", autoErrorSnackbar: false});
  const {list: listProducts} = useApiRequest({baseUrl: "/products", resource: "products", autoErrorSnackbar: false});

  const [tickets, setTickets] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [productNames, setProductNames] = useState({});
  const [filters, setFilters] = useState({});
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);

  const canReadProducts = can(ACTIONS.READ, RESOURCES.PRODUCTS);
  const canWriteTickets = can(ACTIONS.WRITE, RESOURCES.TICKETS);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const listTicketsRef = useRef(listTickets);
  listTicketsRef.current = listTickets;
  const listProductsRef = useRef(listProducts);
  listProductsRef.current = listProducts;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    let failure = null;
    const rows = await fetchAllPages(
      async (params) => {
        const result = await listTicketsRef.current(params, {silentError: true});
        if (!result?.success) {
          failure = result;
          return null;
        }
        return result;
      },
      pickServerFilters(filtersRef.current),
      {pageSize: TICKETS_MAX_PAGE_SIZE}
    );

    if (failure) {
      setTickets([]);
      setLoadError(failure);
    } else {
      setTickets(rows);
      setFetchedAt(Date.now());
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canReadProducts) return;

    let cancelled = false;
    (async () => {
      const products = await fetchAllPages(
        async (params) => {
          const result = await listProductsRef.current(params, {silentError: true});
          return result?.success ? result : null;
        },
        {},
        {pageSize: TICKETS_MAX_PAGE_SIZE}
      );
      if (!cancelled) setProductNames(buildProductNameMap(products));
    })();

    return () => {
      cancelled = true;
    };
  }, [canReadProducts]);

  const decoratedTickets = useMemo(
    () => decorateTickets(tickets, {productNames, now: fetchedAt}),
    [tickets, productNames, fetchedAt]
  );

  const productOptions = useMemo(() => getProductFilterOptions(decoratedTickets), [decoratedTickets]);

  const visibleTickets = useMemo(
    () => filterTicketsClientSide(decoratedTickets, {id: filters.id, product: filters.product}),
    [decoratedTickets, filters.id, filters.product]
  );

  const fields = useMemo(
    () => [
      ...ticketServerSearchFields,
      {field: "id", label: "ID del ticket", type: "text", width: 200},
      {field: "product", label: "Producto", type: "select", width: 200, options: productOptions}
    ],
    [productOptions]
  );

  const handleSearch = useCallback(
    (values) => {
      const nextServer = pickServerFilters(values);
      const currentServer = pickServerFilters(filtersRef.current);
      setFilters(values);
      // Sólo se vuelve a llamar al backend si cambió algún filtro server-side.
      if (JSON.stringify(nextServer) !== JSON.stringify(currentServer)) {
        filtersRef.current = values;
        load();
      }
    },
    [load]
  );

  const handleReset = useCallback(() => {
    const hadServerFilters = Object.keys(pickServerFilters(filtersRef.current)).length > 0;
    setFilters({});
    filtersRef.current = {};
    if (hadServerFilters) load();
  }, [load]);

  const handleCloseTicketDialog = () => setTicketDialogOpen(false);

  const forbidden = loadError && isForbiddenError(loadError.error);
  const unauthorized = loadError?.error?.response?.status === 401;

  const renderContent = () => {
    if (loading) return <LoadingContent title="Cargando tickets..." />;

    if (forbidden) {
      return (
        <Alert severity="warning">
          No tienes permiso para consultar el listado de tickets. Necesitas el scope{" "}
          <strong>identity.tickets.global.read</strong> (roles IDENTITY.Admin o IDENTITY.Manager).
        </Alert>
      );
    }

    if (unauthorized) {
      return <Alert severity="error">Tu sesión no es válida o ha caducado. Vuelve a iniciar sesión.</Alert>;
    }

    if (loadError) {
      return (
        <Alert severity="error">
          No se ha podido cargar el listado de tickets: {loadError.userMessage || "error desconocido."}
        </Alert>
      );
    }

    return (
      <>
        <SearchBar fields={fields} initialValues={filters} onSearch={handleSearch} onReset={handleReset} />

        <Alert severity="warning" sx={{mb: 2}}>
          Los tickets se leen de Redis en el momento de la consulta: todos los que aparecen están vigentes. El backend
          no expone fecha de creación, así que la fecha de expiración es aproximada (ahora + TTL restante).
        </Alert>

        <ListView columns={ticketColumns} data={visibleTickets} getRowId={(row) => row.id} />
      </>
    );
  };

  return (
    <>
      <Window
        title="Tickets"
        topRightButtons={
          <>
            <Button variant="outlined" startIcon={<FiRefreshCw />} onClick={load} disabled={loading}>
              Refrescar
            </Button>

            {canWriteTickets && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FiPlus />}
                onClick={() => setTicketDialogOpen(true)}
              >
                Generar ticket
              </Button>
            )}
          </>
        }
      >
        {renderContent()}
      </Window>

      <Dialog
        open={ticketDialogOpen}
        onClose={handleCloseTicketDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{sx: {backgroundColor: theme.palette.colors.background}}}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.sidebar.background,
            color: theme.palette.windowScreen.title,
            fontWeight: "bold"
          }}
        >
          Generar Ticket
        </DialogTitle>

        <DialogContent sx={{pt: 4, pb: 4, color: theme.palette.text.primary}}>
          <Box sx={{mt: 3}}>
            <TicketForm
              onSuccess={() => {
                handleCloseTicketDialog();
                load();
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Tickets;
